import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  InterviewCard,
  CollectionCard,
  SectionHeader,
  LoadingBlock,
} from "@/components/site/Cards";
import {
  HeroSection,
  ProblemPickerSection,
  WhatIsSection,
  FeaturedInterviewSection,
  FormatsSection,
  ContributorSnippetsSection,
  ForWhomSection,
  WhyItExistsSection,
  NewsletterInline,
} from "@/components/site/HomeSections";

export default function Home() {
  const [data, setData] = useState({ interviews: [], operators: [], topics: [], industries: [], collections: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [interviews, operators, topics, industries, collections] = await Promise.all([
          api.get("/interviews", { params: { limit: 6, sort: "featured" } }),
          api.get("/operators", { params: { featured: true } }),
          api.get("/topics"),
          api.get("/industries"),
          api.get("/collections"),
        ]);
        if (cancelled) return;
        setData({
          interviews: interviews.data,
          operators: operators.data,
          topics: topics.data,
          industries: industries.data,
          collections: collections.data,
        });
      } catch (err) {
        console.error("Failed to load home data:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <LoadingBlock />;

  const featured = data.interviews[0];
  const rest = data.interviews.slice(1, 5);

  return (
    <div data-testid="home-page">
      <HeroSection />
      <ProblemPickerSection />
      <WhatIsSection />
      <FeaturedInterviewSection interview={featured} />
      <FormatsSection />

      {rest.length > 0 && (
        <section className="border-b border-oc-hairline">
          <div className="max-w-editorial mx-auto px-6 md:px-10 py-20 md:py-28">
            <SectionHeader
              eyebrow="Recent articles"
              title="Conversations worth returning to."
              action={<Link to="/read" className="font-ui text-xs font-bold uppercase tracking-widest text-oc-ink hover:text-oc-blue">View archive &rarr;</Link>}
              testId="home-recent-interviews-header"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {rest.map((iv) => <InterviewCard key={iv.slug} interview={iv} />)}
            </div>
          </div>
        </section>
      )}

      <ContributorSnippetsSection operators={data.operators} />
      <ForWhomSection />
      <WhyItExistsSection />

      {data.collections.length > 0 && (
        <section className="border-b border-oc-hairline">
          <div className="max-w-editorial mx-auto px-6 md:px-10 py-20 md:py-28">
            <SectionHeader
              eyebrow="Curated collections"
              title="Guided reading paths, not chronological feeds."
              description="Curated sets of interviews that read best together, grouped by theme, decision type, or operating stage."
              testId="home-collections-header"
            />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {data.collections.slice(0, 4).map((c) => <CollectionCard key={c.slug} collection={c} />)}
            </div>
          </div>
        </section>
      )}

      <NewsletterInline />
    </div>
  );
}
